import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Search, ChevronDown, ChevronUp } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PayoutReconcile } from "./PayoutReconcile";

const STATUS_STYLE = {
  PAID: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  PROCESSING: "border-info/40 bg-info/10 text-info",
  AUTHORIZED: "border-info/40 bg-info/10 text-info",
  AGUARDANDO_AUTORIZACAO: "border-warning/40 bg-warning/10 text-warning",
  AGUARDANDO_SALDO: "border-warning/40 bg-warning/10 text-warning",
  FAILED: "border-destructive/40 bg-destructive/10 text-destructive",
  NOT_ELIGIBLE: "border-white/15 bg-white/5 text-zinc-400",
  PENDING: "border-white/15 bg-white/5 text-zinc-400",
  ENVIANDO: "border-info/40 bg-info/10 text-info",
  INCERTO: "border-warning/60 bg-warning/15 text-warning",
};
const FILTERS = ["all", "INCERTO", "ENVIANDO", "PAID", "PROCESSING", "AGUARDANDO_SALDO", "FAILED", "NOT_ELIGIBLE", "PENDING"];
const FILTER_LABEL = { all: "Todos", INCERTO: "Conferir no Asaas", ENVIANDO: "Enviando", PAID: "Concluídos", PROCESSING: "Processando", AGUARDANDO_SALDO: "Aguardando saldo", FAILED: "Falhas", NOT_ELIGIBLE: "Sem Pix", PENDING: "Pendentes" };

function dt(s) {
  if (!s) return "";
  return new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function PayoutAttempts({ p }) {
  return (
    <div className="mt-3 pl-3 border-l border-white/10 space-y-2" data-testid="payout-attempts">
      {(p.attempts || []).length === 0 ? (
        <div className="text-xs text-zinc-500">Nenhuma tentativa registrada ainda.</div>
      ) : p.attempts.map((a, i) => (
        <div key={i} className="text-xs">
          <div className="flex items-center gap-2">
            <span className={`px-1.5 py-0.5 rounded-xl border text-[10px] font-semibold ${STATUS_STYLE[a.status] || STATUS_STYLE.PENDING}`}>{a.status_label || a.status}</span>
            <span className="text-zinc-500 mono">{dt(a.at)}</span>
          </div>
          {a.gateway && <div className="text-zinc-500 mt-0.5">Gateway: {a.gateway}{a.provider_id ? ` · ID ${a.provider_id}` : ""}</div>}
          {a.error && <div className="text-destructive mt-0.5">{a.error}</div>}
        </div>
      ))}
    </div>
  );
}

export default function AdminPayouts() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ payouts: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/admin/payouts?status=${status}&q=${encodeURIComponent(q)}`).then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [status, q]); // eslint-disable-line
  // Auto-refresh: repasses concluídos saem das filas de pendência automaticamente, sem recarregar.
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); }, [status, q]); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="admin-payouts">
      {(data.counts.INCERTO || 0) + (data.counts.ENVIANDO || 0) > 0 && (
        <button onClick={() => setStatus("INCERTO")} data-testid="payout-uncertain-banner" className="w-full text-left rounded-xl border border-warning/50 bg-warning/10 p-3 text-sm text-warning">
          {(data.counts.INCERTO || 0) + (data.counts.ENVIANDO || 0)} repasse(s) com resultado desconhecido aguardam conferência no Asaas. Clique para ver.
        </button>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`payout-filter-${f}`} onClick={() => setStatus(f)}
            className={`px-3 py-1.5 rounded-xl text-sm border transition-colors ${status === f ? "border-[#FF4D00]/50 bg-[#FF4D00]/10 text-[#FF4D00]" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}>
            {FILTER_LABEL[f]}{f !== "all" && data.counts[f] ? ` (${data.counts[f]})` : ""}
          </button>
        ))}
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <Input data-testid="payout-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por músico ou evento..." className="pl-9 bg-[#09090B] border-white/10 rounded-xl" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" />Carregando...</div>
      ) : data.payouts.length === 0 ? (
        <div className="text-zinc-500 bg-card border border-dashed border-white/10 rounded-xl p-12 text-center flex flex-col items-center gap-2">
          <Send className="h-8 w-8 text-zinc-600" />Nenhum repasse encontrado.
        </div>
      ) : (
        <div className="space-y-2">
          {data.payouts.map((p) => (
            <div key={p.id} data-testid={`payout-row-${p.id}`} className="bg-card border border-white/[0.06] rounded-xl p-4">
              <button onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} className="w-full flex items-center gap-3 text-left">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.musician} <span className="text-zinc-500 font-normal">· {p.event_name}</span></div>
                  <div className="text-xs text-zinc-500 truncate">{p.instrument || "—"}{p.pix_key_masked ? ` · Pix ${p.pix_key_masked}` : ""} · {p.attempt_count} tentativa(s)</div>
                </div>
                <span className="font-mono font-semibold text-accent shrink-0">{money(p.amount || 0)}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-xl border font-semibold shrink-0 ${STATUS_STYLE[p.status] || STATUS_STYLE.PENDING}`}>{p.status_label}</span>
                {open[p.id] ? <ChevronUp className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />}
              </button>
              {open[p.id] && (
                <div className="mt-2">
                  <div className="text-[11px] text-zinc-600 mono">Criado {dt(p.created_at)}{p.updated_at ? ` · Atualizado ${dt(p.updated_at)}` : ""}{p.provider_id ? ` · ID ${p.provider_id}` : ""}</div>
                  {p.error && <div className="text-xs text-destructive mt-1">{p.error}</div>}
                  {["INCERTO", "ENVIANDO"].includes(p.status) && <PayoutReconcile p={p} onDone={load} />}
                  <PayoutAttempts p={p} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
